// Gallery route component. Splits the splat into folder + file,
// fetches the folder listing, filters to media-only siblings, drives
// the carousel, wires gestures and keyboard, and composes the
// subcomponents.
//
// Destructive actions (delete/move/rename) call the worker directly
// and update a local "optimistically removed" set so the carousel
// advances without waiting for refetch.

import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useDirListing } from "@/hooks/useDirListing";
import { useGalleryBytes, type MediaEntry } from "@/hooks/useGalleryBytes";
import { useGestures } from "@/hooks/useGestures";
import { useSession } from "@/stores/session";
import { useInvalidateFolderThumbnails } from "@/hooks/useThumbnail";
import { useVault } from "@/hooks/useVault";
import { scheduleDelete, type DeleteHandle } from "@/lib/deleteUndo";
import { isMediaEntry } from "@/components/browser/entryKind";
import { NewFolderSheet } from "@/components/browser/NewFolderSheet";
import { MediaStage } from "./MediaStage";
import { ThumbnailStrip } from "./ThumbnailStrip";
import { GalleryTopBar } from "./GalleryTopBar";
import { GalleryActionSheet } from "./GalleryActionSheet";

export function GalleryViewer() {
  const { vaultId } = useParams();
  const location = useLocation();
  const session = useSession((s) => s.session);

  // Session gate: deep-link reload lands here unauthenticated.
  if (!session || session.vaultId !== vaultId) {
    const returnTo = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/master?returnTo=${returnTo}`} replace />;
  }
  return <GalleryInner />;
}

function GalleryInner() {
  const { vaultId, "*": splat } = useParams();
  const navigate = useNavigate();
  const worker = useVault();
  const handle = useSession((s) => s.session?.handle ?? null);
  const invalidateFolderThumbnails = useInvalidateFolderThumbnails();

  const { folderPath, fileName } = splitSplat(splat ?? "");
  const { data } = useDirListing(folderPath);

  // Optimistic-removal set for destructive actions (delete/move).
  const [optimisticallyRemoved, setOptimisticallyRemoved] = useState<
    Set<string>
  >(() => new Set());

  const mediaEntries = useMemo(() => {
    if (!data) return [] as { name: string; mime: string; size?: number }[];
    return data.entries
      .filter((e) => isMediaEntry(e))
      .filter((e) => !optimisticallyRemoved.has(e.name))
      .map((e) => ({ name: e.name, mime: e.mime!, size: e.size }));
  }, [data, optimisticallyRemoved]);

  const media: MediaEntry[] = useMemo(
    () =>
      mediaEntries.map((e) => ({
        path: joinPath(folderPath, e.name),
        mime: e.mime,
      })),
    [mediaEntries, folderPath],
  );

  const fileNames = useMemo(
    () => mediaEntries.map((e) => e.name),
    [mediaEntries],
  );

  const [currentIndex, setCurrentIndex] = useState<number>(0);

  // Snap index to the requested fileName; close when nothing remains.
  useEffect(() => {
    if (mediaEntries.length === 0 && data) {
      navigate(-1);
      return;
    }
    const i = mediaEntries.findIndex((e) => e.name === fileName);
    if (i >= 0) {
      setCurrentIndex(i);
    } else {
      // Requested file vanished (deleted or renamed). Clamp and
      // reset zoom/pan since we're showing a different photo now.
      setCurrentIndex((prev) => Math.min(prev, mediaEntries.length - 1));
      setZoom(1);
      setPan({ x: 0, y: 0 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaEntries.length, fileName]);

  const current = mediaEntries[currentIndex];
  const { url, mime, loading, error } = useGalleryBytes(media, currentIndex);

  // Chrome + strip + menu visibility.
  const [stripOpen, setStripOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);

  // Zoom + pan state for image stage.
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const setZoomRef = useRef<(z: number) => void>(() => {});

  // Advance with zoom/pan reset and URL update.
  const advanceTo = useCallback(
    (next: number) => {
      if (next < 0 || next >= mediaEntries.length) return;
      setCurrentIndex(next);
      setZoom(1);
      setPan({ x: 0, y: 0 });
      setZoomRef.current(1);
      const nextName = mediaEntries[next]!.name;
      navigate(
        `/v/${vaultId}/view/${encodePath(joinPath(folderPath, nextName))}`,
        { replace: true },
      );
    },
    [mediaEntries, navigate, vaultId, folderPath],
  );

  // Gesture wiring. `useGestures` emits incremental dx/dy deltas
  // (since the last pointer-move, not since pointer-down), so we
  // accumulate them into pan state with the functional updater.
  const { pointerDown, pointerMove, pointerUp, setZoom: setInternalZoom } =
    useGestures({
      onSwipe: (dir) => {
        if (dir === "left") advanceTo(currentIndex + 1);
        else advanceTo(currentIndex - 1);
      },
      onPan: (dx, dy) =>
        setPan((prev) => ({ x: prev.x + dx, y: prev.y + dy })),
      onZoomChange: (z) => setZoom(z),
      onDoubleTapZoom: () => {
        if (zoom > 1) {
          setZoom(1);
          setPan({ x: 0, y: 0 });
          setInternalZoom(1);
        } else {
          setZoom(2);
          setInternalZoom(2);
        }
      },
      onCloseRequest: () => navigate(-1),
      initialZoom: 1,
    });
  setZoomRef.current = setInternalZoom;

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      pointerDown({ id: e.pointerId, x: e.clientX, y: e.clientY });
    },
    [pointerDown],
  );

  // Desktop keyboard. Skip when a dialog (rename, etc.) is open so
  // keypresses go to the dialog's input instead of triggering gallery
  // shortcuts like delete-on-Backspace.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (renameOpen) return;
      if (e.key === "Escape") navigate(-1);
      else if (e.key === "ArrowLeft" && zoom === 1)
        advanceTo(currentIndex - 1);
      else if (e.key === "ArrowRight" && zoom === 1)
        advanceTo(currentIndex + 1);
      else if (e.key === "Delete") {
        if (current) onDelete(current.name);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, zoom, current?.name, renameOpen]);

  // Desktop scroll-wheel zoom. React attaches wheel listeners
  // passively by default, so preventDefault() in an onWheel handler
  // is a no-op. Bind manually with { passive: false } via a ref so
  // we can both stop page-scroll AND apply the zoom.
  const stageRootRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = stageRootRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (mime?.startsWith("video/")) return;
      e.preventDefault();
      const next = clamp(zoom - e.deltaY * 0.002, 1, 8);
      setZoom(next);
      setInternalZoom(next);
      if (next === 1) {
        setPan({ x: 0, y: 0 });
      }
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [mime, zoom, setInternalZoom]);

  // Destructive actions.
  const onDelete = useCallback(
    (name: string) => {
      if (handle === null) return;
      const path = joinPath(folderPath, name);
      setOptimisticallyRemoved((prev) => new Set(prev).add(name));
      const h: DeleteHandle = scheduleDelete({
        delayMs: 6000,
        onCommit: async () => {
          try {
            await worker.deleteEntry(handle, path);
          } catch (err) {
            toast.error(
              `Delete failed: ${err instanceof Error ? err.message : String(err)}`,
            );
            setOptimisticallyRemoved((prev) => {
              const next = new Set(prev);
              next.delete(name);
              return next;
            });
          }
        },
      });
      toast(`Deleted ${name}`, {
        duration: 6000,
        action: {
          label: "Undo",
          onClick: () => {
            h.cancel();
            setOptimisticallyRemoved((prev) => {
              const next = new Set(prev);
              next.delete(name);
              return next;
            });
          },
        },
      });
    },
    [handle, worker, folderPath],
  );

  const onRename = useCallback(
    async (name: string, newName: string) => {
      if (handle === null || name === newName) return;
      try {
        await worker.renameEntry(handle, joinPath(folderPath, name), newName);
        await worker.renameThumbnailEntry(handle, folderPath, name, newName);
        invalidateFolderThumbnails(folderPath);
        navigate(
          `/v/${vaultId}/view/${encodePath(joinPath(folderPath, newName))}`,
          { replace: true },
        );
      } catch (err) {
        toast.error(
          `Rename failed: ${err instanceof Error ? err.message : String(err)}`,
        );
      }
    },
    [handle, worker, folderPath, navigate, vaultId, invalidateFolderThumbnails],
  );

  if (!current) return null;

  const indexLabel = `${currentIndex + 1} of ${mediaEntries.length}`;
  const sizeLabel = current.size ? humanBytes(current.size) : "";

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-black text-white"
      onClick={(e) => {
        if (e.target === e.currentTarget) setStripOpen((s) => !s);
      }}
    >
      {/* Top chrome: always visible */}
      <div className="pointer-events-auto">
        <GalleryTopBar
          fileName={current.name}
          indexLabel={indexLabel}
          sizeLabel={sizeLabel}
          onBack={() => navigate(-1)}
          onMore={() => setMenuOpen(true)}
        />
      </div>

      {/* Stage. `min-h-0` is load-bearing: without it this flex item
          grows to fit the natural pixel size of the underlying image,
          so `max-h-full` on the <img> resolves against an unbounded
          parent and the photo overflows the viewport. */}
      <div
        ref={stageRootRef}
        className="min-h-0 flex-1"
        onClick={(e) => {
          if (e.target === e.currentTarget) setStripOpen((s) => !s);
        }}
      >
        <MediaStage
          url={url}
          mime={mime}
          loading={loading}
          error={error}
          zoom={zoom}
          panX={pan.x}
          panY={pan.y}
          onPointerDown={handlePointerDown}
          onPointerMove={(e) =>
            pointerMove({ id: e.pointerId, x: e.clientX, y: e.clientY })
          }
          onPointerUp={(e) =>
            pointerUp({ id: e.pointerId, x: e.clientX, y: e.clientY })
          }
        />
      </div>

      {/* Desktop edge arrows (hidden on mobile via CSS) */}
      <button
        type="button"
        onClick={() => advanceTo(currentIndex - 1)}
        className="absolute left-3 top-1/2 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-black/45 text-2xl text-white hover:bg-black/65 md:flex"
        aria-label="Previous"
      >
        ‹
      </button>
      <button
        type="button"
        onClick={() => advanceTo(currentIndex + 1)}
        className="absolute right-3 top-1/2 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-black/45 text-2xl text-white hover:bg-black/65 md:flex"
        aria-label="Next"
      >
        ›
      </button>

      {/* Bottom strip: toggles on tap */}
      {stripOpen && mediaEntries.length > 1 && (
        <div className="pointer-events-auto bg-gradient-to-t from-black/80 to-transparent">
          <ThumbnailStrip
            folderPath={folderPath}
            fileNames={fileNames}
            activeIndex={currentIndex}
            onPick={advanceTo}
          />
        </div>
      )}

      <GalleryActionSheet
        open={menuOpen}
        onOpenChange={setMenuOpen}
        fileName={current.name}
        onExport={() => {
          setMenuOpen(false);
          toast.info(
            "Export from gallery coming soon — use the browser's export for now.",
          );
        }}
        onRename={() => {
          setMenuOpen(false);
          setRenameOpen(true);
        }}
        onMove={() => {
          setMenuOpen(false);
          toast.info("Move from gallery coming soon — use the browser.");
        }}
        onDelete={() => {
          setMenuOpen(false);
          onDelete(current.name);
        }}
      />

      {renameOpen && (
        <NewFolderSheet
          mode="rename-file"
          destPath={folderPath}
          siblings={(data?.entries ?? [])
            .filter((e) => e.name !== current.name)
            .map((e) => e.name)}
          initialName={current.name}
          open
          onOpenChange={(next) => {
            if (!next) setRenameOpen(false);
          }}
          onSubmit={async (newName) => {
            setRenameOpen(false);
            if (newName && newName !== current.name) {
              await onRename(current.name, newName);
            }
          }}
        />
      )}
    </div>
  );
}

// ————— helpers —————

function splitSplat(splat: string): { folderPath: string; fileName: string } {
  // React Router v6 already decodes splat params, so do NOT
  // decodeURIComponent here — that would double-decode any `%`,
  // `+`, or other reserved chars in filenames.
  const idx = splat.lastIndexOf("/");
  if (idx < 0) return { folderPath: "/", fileName: splat };
  const folder = splat.slice(0, idx) || "/";
  const file = splat.slice(idx + 1);
  return {
    folderPath: folder.startsWith("/") ? folder : `/${folder}`,
    fileName: file,
  };
}

function joinPath(folder: string, name: string): string {
  return `${folder === "/" ? "" : folder}/${name}`.replace(/\/+/g, "/");
}

function encodePath(path: string): string {
  return path
    .split("/")
    .filter((s) => s.length > 0)
    .map(encodeURIComponent)
    .join("/");
}

function humanBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KiB`;
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / (1024 * 1024)).toFixed(1)} MiB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GiB`;
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}
